import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Pressable, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Grabber } from '../components/Grabber'
import { SelectedChainRow } from '../components/SelectedChainRow'
import {
  FigmaChevronLeftIcon,
  FigmaCopyIcon,
  FigmaQrScanIcon,
  FigmaShareIcon,
} from '../icons/FigmaSystemIcons'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'

/**
 * The Transfer Crypto / Universal-Deposit-Address screen. A themed bottom-sheet
 * surface: a back + QR top bar, the tappable selected-chain card, a centered
 * deposit-address card with the chain's minimum, and full-width Copy / Share
 * buttons. UI only — the host wires the data + callbacks (the deposit-address
 * resolution lives in the transfer hooks).
 */
export interface TransferCryptoScreenProps {
  /** Pre-formatted deposit address, e.g. `0x7854...5139F0`. */
  depositAddress: string
  /** The chain the address was generated for, with its minimum-deposit label. */
  selectedChain: { name: string; iconUri: string; minLabel?: string }
  /** How long the "Copied" confirmation stays on the Copy button (ms). */
  copiedFeedbackMs?: number
  /** Leading top-bar action — returns to the Add money screen. */
  onBack?: () => void
  onShowQrCode?: () => void
  /** Tapping the selected-chain card (opens the chain picker). */
  onChangeChain?: () => void
  onCopyAddress?: () => void
  onShareAddress?: () => void
}

export function TransferCryptoScreen(props: TransferCryptoScreenProps) {
  const { t } = useFunkitTranslation()
  const {
    depositAddress,
    selectedChain,
    copiedFeedbackMs = 1800,
    onBack,
    onShowQrCode,
    onChangeChain,
    onCopyAddress,
    onShareAddress,
  } = props
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const [copied, setCopied] = useState(false)
  const copyTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => () => clearTimeout(copyTimer.current), [])

  const handleCopy = () => {
    onCopyAddress?.()
    setCopied(true)
    clearTimeout(copyTimer.current)
    copyTimer.current = setTimeout(() => setCopied(false), copiedFeedbackMs)
  }

  return (
    <Box
      borderTopLeftRadius="modal"
      borderTopRightRadius="modal"
      style={{ backgroundColor: theme.background.modal }}
    >
      {/* Top bar: back · title · QR */}
      <Box flexDirection="row" alignItems="center" style={styles.topBar}>
        <Grabber />
        <Pressable
          onPress={onBack}
          hitSlop={12}
          accessibilityRole="button"
          accessibilityLabel={t('common.back')}
        >
          <Box
            alignItems="center"
            justifyContent="center"
            style={styles.iconButton}
          >
            <FigmaChevronLeftIcon />
          </Box>
        </Pressable>
        <Box flex={1} alignItems="center">
          <Text
            variant="bodyMedium"
            style={{ color: theme.content.elements.primary }}
          >
            {t('transferCrypto.title')}
          </Text>
        </Box>
        <Pressable
          onPress={onShowQrCode}
          hitSlop={12}
          accessibilityRole="button"
          accessibilityLabel={t('transferCrypto.showQrCode')}
        >
          <Box
            alignItems="center"
            justifyContent="center"
            style={styles.iconButton}
          >
            <FigmaQrScanIcon />
          </Box>
        </Pressable>
      </Box>

      <Box style={[styles.content, { paddingBottom: insets.bottom + 16 }]}>
        <SelectedChainRow
          label={t('transferCrypto.selectedChain')}
          name={selectedChain.name}
          iconUri={selectedChain.iconUri}
          accessibilityLabel={t('transferCrypto.changeChain')}
          onPress={onChangeChain}
        />

        <Box alignItems="center" style={styles.addressRegion}>
          <Box
            alignItems="center"
            justifyContent="center"
            borderWidth={1}
            borderRadius="element"
            style={[
              styles.addressCard,
              {
                backgroundColor: theme.background.modal,
                borderColor: theme.border.elements.default.color,
              },
            ]}
          >
            <Box alignItems="center" style={styles.addressColumn}>
              <Box alignItems="center" style={styles.addressGroup}>
                <Text
                  variant="detail"
                  style={{ color: theme.content.elements.secondary }}
                >
                  {t('transferCrypto.depositAddressLabel')}
                </Text>
                <Text
                  variant="bodyMedium"
                  style={[
                    styles.addressText,
                    { color: theme.content.elements.primary },
                  ]}
                >
                  {depositAddress}
                </Text>
              </Box>
              {selectedChain.minLabel ? (
                <Text
                  variant="body"
                  style={{ color: theme.content.elements.secondary }}
                >
                  {selectedChain.minLabel}
                </Text>
              ) : null}
            </Box>
          </Box>

          {/* Copy / Share */}
          <Box flexDirection="row" style={styles.buttonsRow}>
            <PillButton
              label={copied ? t('common.copied') : t('common.copy')}
              accessibilityLabel={
                copied ? t('common.addressCopied') : t('common.copyAddress')
              }
              icon={
                <FigmaCopyIcon color={theme.content.button.primary.default} />
              }
              onPress={handleCopy}
            />
            <PillButton
              label={t('transferCrypto.share')}
              accessibilityLabel={t('transferCrypto.shareAddress')}
              icon={
                <FigmaShareIcon color={theme.content.button.primary.default} />
              }
              onPress={onShareAddress}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

/** A full-width black pill action button (Copy / Share). */
function PillButton({
  label,
  accessibilityLabel,
  icon,
  onPress,
}: {
  label: string
  accessibilityLabel: string
  icon: ReactNode
  onPress?: () => void
}) {
  const theme = useTheme()
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={styles.pillButtonPress}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        borderRadius="button"
        style={[
          styles.pillButton,
          { backgroundColor: theme.background.button.primary.default },
        ]}
      >
        {icon}
        <Text
          variant="bodyMedium"
          style={{ color: theme.content.button.primary.default }}
        >
          {label}
        </Text>
      </Box>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  topBar: {
    paddingTop: 16,
    paddingBottom: 16,
    paddingHorizontal: 24,
    gap: 4,
  },
  // 32px touch target centers the 20px glyph so it visually aligns with the
  // px-20 content below.
  iconButton: { width: 32, height: 32 },
  content: {
    paddingHorizontal: 20,
    // paddingBottom is set inline: safe-area inset + 16 so the buttons keep a
    // 16px gap above the home indicator on every device.
    paddingTop: 8,
    gap: 16,
  },
  addressRegion: { gap: 12, width: '100%' },
  addressCard: {
    width: '100%',
    // Centres the address block in the card at the designed height.
    paddingVertical: 148,
    paddingHorizontal: 20,
    // The card MUST keep an opaque `backgroundColor`: on Android `elevation`
    // draws its shadow from the view outline, and without a solid background it
    // can't clip to the rounded corners — it bleeds as a gray rectangular
    // border around the card.
    shadowColor: '#000',
    shadowOpacity: 0.02,
    shadowOffset: { width: 0, height: 8 },
    shadowRadius: 32,
    elevation: 1,
  },
  // 6 inside the label+address pair, 8 out to the minimum.
  addressColumn: { gap: 8 },
  addressGroup: { gap: 6 },
  // Weight (medium) comes from the `bodyMedium` variant's Inter-Medium family —
  // an inline `fontWeight` wouldn't select the cut on a custom font in RN/Expo.
  addressText: { fontSize: 28, lineHeight: 39 },
  buttonsRow: { gap: 10, width: '100%' },
  pillButtonPress: { flex: 1 },
  pillButton: { height: 56, paddingHorizontal: 24, gap: 8 },
})
